import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  IconButton,
  Link,
  Divider,
} from '@mui/material';
import {
  Facebook,
  Twitter,
  LinkedIn,
  Instagram,
  Email,
  Phone,
  LocationOn,
} from '@mui/icons-material';
import { getLanguage, t } from '@/utils/i18n-enhanced';

const PublicFooter: React.FC = () => {
  const language = getLanguage();

  return (
    <Box sx={{ bgcolor: 'grey.900', color: 'white', pt: 8, pb: 4 }}>
      <Container maxWidth="lg">
        <Grid container spacing={6}>
          <Grid item xs={12} md={4}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
              souk.immo
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, opacity: 0.8, lineHeight: 1.7 }}>
              {t('footerTagline')}
            </Typography>
            <Stack direction="row" spacing={2}>
              <IconButton sx={{ color: 'white' }}><Facebook /></IconButton>
              <IconButton sx={{ color: 'white' }}><Twitter /></IconButton>
              <IconButton sx={{ color: 'white' }}><LinkedIn /></IconButton>
              <IconButton sx={{ color: 'white' }}><Instagram /></IconButton>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              {language === 'en' ? 'Explore' : language === 'fr' ? 'Explorer' : 'استكشف'}
            </Typography>
            <Stack spacing={1}>
              <Link href="/properties" color="inherit" sx={{ textDecoration: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                {language === 'en' ? 'Properties' : language === 'fr' ? 'Propriétés' : 'عقارات'}
              </Link>
              <Link href="/chantiers" color="inherit" sx={{ textDecoration: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                {language === 'en' ? 'Developments' : language === 'fr' ? 'Développements' : 'مشاريع'}
              </Link>
              <Link href="/crm" color="inherit" sx={{ textDecoration: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                {language === 'en' ? 'For Agencies' : language === 'fr' ? 'Pour Agences' : 'للوكالات'}
              </Link>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              {language === 'en' ? 'Company' : language === 'fr' ? 'Entreprise' : 'الشركة'}
            </Typography>
            <Stack spacing={1}>
              <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                {language === 'en' ? 'About' : language === 'fr' ? 'À propos' : 'حول'}
              </Link>
              <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                {language === 'en' ? 'Blog' : 'Blog'}
              </Link>
              <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                {language === 'en' ? 'Careers' : language === 'fr' ? 'Carrières' : 'وظائف'}
              </Link>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              {language === 'en' ? 'Support' : language === 'fr' ? 'Support' : 'الدعم'}
            </Typography>
            <Stack spacing={1}>
              <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                {language === 'en' ? 'Help Center' : language === 'fr' ? 'Centre d\'aide' : 'مركز المساعدة'}
              </Link>
              <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                {language === 'en' ? 'Contact' : language === 'fr' ? 'Contact' : 'اتصل بنا'}
              </Link>
              <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.8, '&:hover': { opacity: 1 } }}>
                {language === 'en' ? 'Privacy' : language === 'fr' ? 'Confidentialité' : 'الخصوصية'}
              </Link>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              {language === 'en' ? 'Contact' : language === 'fr' ? 'Contact' : 'اتصل بنا'}
            </Typography>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.8 }}>
                <Email fontSize="small" />
                <Typography variant="body2">contact@souk.immo</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.8 }}>
                <Phone fontSize="small" />
                <Typography variant="body2">+212 (0) 5 30 20 2005</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.8 }}>
                <LocationOn fontSize="small" />
                <Typography variant="body2">
                  {language === 'en' ? 'Rabat , Morocco' : language === 'fr' ? 'Rabat, Maroc' : 'الرباط، المغرب'}
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>
        <Divider sx={{ my: 6, borderColor: 'rgba(255,255,255,0.1)' }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            © {new Date().getFullYear()} souk.immo. {t('footerRights')}.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.7, fontSize: '0.875rem', '&:hover': { opacity: 1 } }}>
              {language === 'en' ? 'Terms' : language === 'fr' ? 'Conditions' : 'الشروط'}
            </Link>
            <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.7, fontSize: '0.875rem', '&:hover': { opacity: 1 } }}>
              {language === 'en' ? 'Privacy' : language === 'fr' ? 'Confidentialité' : 'الخصوصية'}
            </Link>
            <Link href="#" color="inherit" sx={{ textDecoration: 'none', opacity: 0.7, fontSize: '0.875rem', '&:hover': { opacity: 1 } }}>
              {language === 'en' ? 'Cookies' : language === 'fr' ? 'Cookies' : 'ملفات تعريف الارتباط'}
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PublicFooter;
